// useOutlineKeyboard — keyboard navigation for chapter outline lists.
//
// Shared between outline-view.tsx (read-only outline viewer) and
// outline-review.tsx (editable outline review). Both had ~20 lines of
// near-identical keydown handler logic (ArrowUp/Down for navigation,
// Enter to toggle expand, Escape to collapse all).
//
// Key improvement over the inline versions: uses isTypingTarget() from
// lib/keyboard.ts instead of a bare tagName check — prevents keystroke
// hijacking inside contenteditable editors and during IME composition.
//
// Session 16 · T4.1.

import { useEffect } from 'react';
import { isTypingTarget } from '../keyboard.js';

export function useOutlineKeyboard(
  isActive: boolean,
  config: {
    /** Total number of chapters in the list. */
    chapterCount: number;
    /** Current focused chapter index. */
    focusedIdx: number;
    /** Called with the new index when ArrowUp/ArrowDown is pressed. */
    setFocusedIdx: (update: (prev: number) => number) => void;
    /** Called with the chapter id when Enter is pressed on a focused chapter. */
    onToggleChapter: (chapterId: string) => void;
    /** Returns the chapter id for a given index. */
    getChapterId: (idx: number) => string;
    /** Collapse all expanded chapters + clear focus. */
    onCollapseAll: () => void;
  },
): void {
  useEffect(() => {
    if (!isActive) return;
    const { chapterCount, focusedIdx, setFocusedIdx, onToggleChapter, getChapterId, onCollapseAll } = config;

    const handler = (e: KeyboardEvent) => {
      if (isTypingTarget(e)) return;

      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setFocusedIdx((i) => Math.min(i + 1, chapterCount - 1));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setFocusedIdx((i) => Math.max(i - 1, 0));
      } else if (e.key === 'Enter' && focusedIdx >= 0 && focusedIdx < chapterCount) {
        e.preventDefault();
        onToggleChapter(getChapterId(focusedIdx));
      } else if (e.key === 'Escape') {
        onCollapseAll();
      }
    };

    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [isActive, config]);
}
